const UseFormContextUtils = module.exports = {};
const UseFormConfigUtils = require('./config');
const InvalidSubmitFocusUtils = require('hooks/useFormPrimitives/invalidSubmitFocusUtils');
const hoverFieldHighlightStyles = require('hooks/useFormPrimitives/hoverFieldHighlightStyles');
const internals = {};

UseFormContextUtils.buildFieldContextValue = ({
    formConfig,
    formModel,
    formMethods,
    actions = {},
    definitions = {},
    inputRefs = {},
    preferencesHandle
}) => {

    return {
        actions,
        definitions,
        formConfig,
        formMethods,
        formModel,
        inputRefs,
        preferencesHandle
    };
};

UseFormContextUtils.getFieldDefinition = ({
    contextValue,
    fieldName,
    getDefaultFieldDefinition
}) => {

    return contextValue?.definitions?.[fieldName] || getDefaultFieldDefinition(fieldName);
};

UseFormContextUtils.getFieldDefault = ({
    contextValue,
    fieldName
}) => {

    return contextValue?.formModel?.defaults?.[fieldName];
};

UseFormContextUtils.getPath = (obj, path) => {

    if (!path) {
        return obj;
    }

    return String(path).split('.').reduce((current, key) => current?.[key], obj);
};

UseFormContextUtils.getFieldValue = ({
    contextValue,
    fieldName,
    watchedValue
}) => {
    if (typeof watchedValue !== 'undefined') {
        return watchedValue;
    }

    if (typeof contextValue?.formMethods?.getValues === 'function') {
        return contextValue.formMethods.getValues(fieldName);
    }

    return UseFormContextUtils.getFieldDefault({
        contextValue,
        fieldName
    });
};

UseFormContextUtils.getFieldError = ({
    contextValue,
    fieldName,
    fieldState
}) => UseFormContextUtils.getPath(contextValue?.formMethods?.__manualFieldErrors, fieldName)
    || UseFormContextUtils.getPath(fieldState?.errors, fieldName)
    || fieldState?.error
    || UseFormContextUtils.getPath(contextValue?.formMethods?.formState?.errors, fieldName)
    || null;

UseFormContextUtils.getValidationLabel = (definition = {}) => definition.validationLabel || definition.label || definition.name || 'Field';

UseFormContextUtils.buildRequiredMessage = (definition = {}) => `${UseFormContextUtils.getValidationLabel(definition)} is required`;

UseFormContextUtils.buildMaxLengthMessage = (definition = {}) => `${UseFormContextUtils.getValidationLabel(definition)} may only be ${definition.maxLength} characters long`;

UseFormContextUtils.buildFieldRules = (definition = {}) => {
    const rules = {
        ...(definition.rules || {})
    };

    if (definition.skipDefaultValidation) {
        return rules;
    }

    if (definition.required && typeof rules.required === 'undefined') {
        rules.required = UseFormContextUtils.buildRequiredMessage(definition);
    }

    if (definition.maxLength && typeof rules.maxLength === 'undefined') {
        rules.maxLength = {
            value: definition.maxLength,
            message: UseFormContextUtils.buildMaxLengthMessage(definition)
        };
    }

    return rules;
};

UseFormContextUtils.getDefinitionHelperText = (definition = {}) => definition.helperText
    || definition.textFieldProps?.helperText
    || null;

UseFormContextUtils.getFieldHelperText = ({
    definition = {},
    fieldError
}) => fieldError?.message || UseFormContextUtils.getDefinitionHelperText(definition);

UseFormContextUtils.mergeTextFieldStyles = (...styleValues) => {
    const fieldStyles = styleValues.filter(Boolean);

    if (!fieldStyles.length) {
        return undefined;
    }

    return fieldStyles.flat();
};

UseFormContextUtils.buildTextFieldProps = ({
    definition = {},
    fieldError,
    inputRef,
    textFieldProps = {}
}) => {
    const hasError = !!fieldError;

    const definitionTextFieldProps = definition.textFieldProps || {};
    const resolvedHelperText = fieldError?.message
        || textFieldProps.helperText
        || definition.helperText
        || definitionTextFieldProps.helperText
        || null;

    return {
        variant: 'filled',
        ...definitionTextFieldProps,
        ...textFieldProps,
        fieldStyles: UseFormContextUtils.mergeTextFieldStyles(
            hoverFieldHighlightStyles,
            definitionTextFieldProps.fieldStyles,
            textFieldProps.fieldStyles
        ),
        inputRef: textFieldProps.inputRef || definitionTextFieldProps.inputRef || inputRef,
        error: !!(hasError || textFieldProps.error || definitionTextFieldProps.error),
        helperText: resolvedHelperText,
        inputProps: {
            ...(definitionTextFieldProps.inputProps || {}),
            ...(textFieldProps.inputProps || {}),
            'aria-invalid': hasError || textFieldProps.inputProps?.['aria-invalid'] || undefined
        }
    };
};

UseFormContextUtils.notifyFieldChange = ({
    contextValue,
    fieldName,
    value,
    event,
    field
}) => {

    if (typeof contextValue?.actions?.onFieldChange !== 'function') {
        UseFormConfigUtils.runFieldEffectSideEffects({
            field,
            context: {
                event,
                field,
                fieldName,
                formConfig: contextValue?.formConfig,
                formMethods: contextValue?.formMethods,
                formModel: contextValue?.formModel,
                formProps: contextValue?.formConfig?.context?.props || {},
                value
            }
        });
        return;
    }

    contextValue.actions.onFieldChange({
        field,
        fieldName,
        value,
        event
    });

    UseFormConfigUtils.runFieldEffectSideEffects({
        field,
        context: {
            event,
            field,
            fieldName,
            formConfig: contextValue?.formConfig,
            formMethods: contextValue?.formMethods,
            formModel: contextValue?.formModel,
            formProps: contextValue?.formConfig?.context?.props || {},
            value
        }
    });
};

UseFormContextUtils.notifyFieldFocus = ({
    contextValue,
    fieldName,
    event,
    field
}) => {

    if (!InvalidSubmitFocusUtils.shouldPreserveErrorOnFocus(event)) {
        internals.clearFieldError({
            fieldName,
            formMethods: contextValue?.formMethods
        });
    }

    if (typeof contextValue?.actions?.onFieldFocus !== 'function') {
        return;
    }

    contextValue.actions.onFieldFocus({
        field,
        fieldName,
        event
    });
};

UseFormContextUtils.notifyFieldBlur = ({
    contextValue,
    fieldName,
    event,
    field
}) => {

    internals.revalidateFieldIfNeeded({
        fieldName,
        formMethods: contextValue?.formMethods
    });

    if (typeof contextValue?.actions?.onFieldBlur !== 'function') {
        return;
    }

    contextValue.actions.onFieldBlur({
        field,
        fieldName,
        event
    });
};

UseFormContextUtils.setFieldValue = ({
    contextValue,
    fieldName,
    value,
    event,
    field,
    options = {
        shouldDirty: true,
        shouldValidate: true
    }
}) => {

    if (typeof contextValue?.formMethods?.setValue !== 'function') {
        return false;
    }

    if (contextValue.formMethods.__manualFieldErrors?.[fieldName]) {
        delete contextValue.formMethods.__manualFieldErrors[fieldName];
        contextValue.formMethods.__manualFieldErrorsVersion = Date.now();
        contextValue.formMethods.clearErrors?.(fieldName);
    }

    contextValue.formMethods.setValue(fieldName, value, options);
    UseFormContextUtils.notifyFieldChange({
        contextValue,
        fieldName,
        value,
        event,
        field
    });
    return true;
};

internals.revalidateOnBlurFieldsByForm = new WeakMap();
internals.fallbackRevalidateOnBlurFields = new Set();

internals.getRevalidateOnBlurFields = (formMethods) => {

    if (!formMethods || typeof formMethods !== 'object') {
        return internals.fallbackRevalidateOnBlurFields;
    }

    const existingFields = internals.revalidateOnBlurFieldsByForm.get(formMethods);

    if (existingFields) {
        return existingFields;
    }

    const fields = new Set();
    internals.revalidateOnBlurFieldsByForm.set(formMethods, fields);
    return fields;
};

internals.hasFieldError = ({
    fieldName,
    formMethods = {}
}) => !!UseFormContextUtils.getPath(formMethods.__manualFieldErrors, fieldName)
    || !!formMethods.getFieldState?.(fieldName)?.error
    || !!UseFormContextUtils.getPath(formMethods.formState?.errors, fieldName);

internals.clearManualFieldError = ({
    fieldName,
    formMethods = {}
}) => {

    if (!fieldName || !formMethods.__manualFieldErrors || typeof formMethods.__manualFieldErrors !== 'object') {
        return false;
    }

    const path = String(fieldName).split('.');
    const parentPath = path.slice(0, -1);
    const fieldKey = path[path.length - 1];
    const parentValue = parentPath.length
        ? UseFormContextUtils.getPath(formMethods.__manualFieldErrors, parentPath.join('.'))
        : formMethods.__manualFieldErrors;

    if (!parentValue || typeof parentValue !== 'object' || !Object.prototype.hasOwnProperty.call(parentValue, fieldKey)) {
        return false;
    }

    delete parentValue[fieldKey];
    formMethods.__manualFieldErrorsVersion = Date.now();
    return true;
};

internals.clearFieldError = ({
    fieldName,
    formMethods = {}
}) => {

    if (!fieldName || !internals.hasFieldError({
        fieldName,
        formMethods
    })) {
        return;
    }

    internals.clearManualFieldError({
        fieldName,
        formMethods
    });

    formMethods.clearErrors?.(fieldName);
    internals.getRevalidateOnBlurFields(formMethods).add(fieldName);
};

internals.revalidateFieldIfNeeded = ({
    fieldName,
    formMethods = {}
}) => {

    if (!fieldName) {
        return;
    }

    const revalidateOnBlurFields = internals.getRevalidateOnBlurFields(formMethods);

    if (!revalidateOnBlurFields.has(fieldName)) {
        return;
    }

    revalidateOnBlurFields.delete(fieldName);
    formMethods.trigger?.(fieldName);
};

UseFormContextUtils.buildFieldController = ({
    contextValue,
    fieldName,
    getDefaultFieldDefinition,
    defaultDefinition,
    watchedValue,
    fieldState
}) => {

    const definition = defaultDefinition || UseFormContextUtils.getFieldDefinition({
        contextValue,
        fieldName,
        getDefaultFieldDefinition
    });
    const fieldError = UseFormContextUtils.getFieldError({
        contextValue,
        fieldName,
        fieldState
    });
    const inputRef = contextValue?.inputRefs?.[definition.refName || fieldName];

    if (typeof window !== 'undefined' && window.__USER_INPUTS_DEBUG_FIELDS__) {
        // eslint-disable-next-line no-console
        console.log('[__USER_INPUTS_DEBUG__ field controller]', fieldName, {
            fieldError,
            fieldState,
            formStateError: UseFormContextUtils.getPath(contextValue?.formMethods?.formState?.errors, fieldName),
            getFieldState: contextValue?.formMethods?.getFieldState?.(fieldName, contextValue?.formMethods?.formState)
        });
    }

    return {
        actions: contextValue?.actions || {},
        definition,
        formConfig: contextValue?.formConfig,
        formProps: contextValue?.formConfig?.context?.props || {},
        inputRef,
        defaultValue: UseFormContextUtils.getFieldDefault({
            contextValue,
            fieldName
        }),
        getValue: () => UseFormContextUtils.getFieldValue({
            contextValue,
            fieldName,
            watchedValue
        }),
        getError: () => fieldError?.message || null,
        getHelperText: () => UseFormContextUtils.getFieldHelperText({
            definition,
            fieldError
        }),
        getTextFieldProps: (textFieldProps = {}) => UseFormContextUtils.buildTextFieldProps({
            definition,
            fieldError,
            inputRef,
            textFieldProps
        }),
        hasError: () => !!fieldError,
        setValue: (value, options, event) => UseFormContextUtils.setFieldValue({
            contextValue,
            fieldName,
            value,
            options,
            event,
            field: definition
        }),
        onFocus: (event) => UseFormContextUtils.notifyFieldFocus({
            contextValue,
            fieldName,
            event,
            field: definition
        }),
        onBlur: (event) => UseFormContextUtils.notifyFieldBlur({
            contextValue,
            fieldName,
            event,
            field: definition
        })
    };
};
